/**
 * D-143 Option C — the browser-scoped recovery-session identifier.
 *
 * WHAT IS STORED. One opaque, server-minted UUID. Nothing else: no email address,
 * no OTP, no password, no reset token, no account or tenant identifier, no
 * authentication material of any kind. On its own the value grants nothing — it
 * addresses a Redis counter whose only content is a small integer.
 *
 * WHY localStorage (Owner ruling 2). The id must survive a page refresh, a second
 * tab and a browser restart, because a counter that silently resets would tell the
 * user something false about how many requests they have made today.
 * `sessionStorage` survives none of those beyond a refresh.
 *
 * WHAT IT IS NOT. The counter behind this id is a DISPLAY. Clearing it, opening a
 * private window or switching browsers changes what the screen shows and changes
 * nothing about what the server will do: the authoritative limits stay 5 OTPs per
 * account per UTC day and a 60-second cooldown, both enforced server-side. Nothing
 * here may ever be treated as a security control.
 *
 * Every access is wrapped: Safari private mode and hardened browser settings throw
 * on `localStorage`, and a password reset must not break because a counter could
 * not be persisted.
 */

const STORAGE_KEY = 'sp-recovery-session';

/** Matches what the server mints. A malformed value is dropped rather than sent. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The stored id, or `undefined` when absent, unreadable or malformed. */
export function readRecoverySessionId(): string | undefined {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && UUID_RE.test(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Persists the id the server issued. Called after every initiation response, so a
 * session that expired at UTC midnight is replaced by the fresh one the server
 * minted rather than being presented forever.
 */
export function writeRecoverySessionId(id: string | undefined): void {
  if (!id || !UUID_RE.test(id)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Storage unavailable — the counter restarts next time. Harmless: the server
    // is the only thing that decides whether an OTP is issued.
  }
}

/** The header the id travels in. A header, so it stays out of request bodies. */
export const RECOVERY_SESSION_HEADER = 'X-Recovery-Session';

/** Request headers carrying the stored id, or `{}` when there is nothing to send. */
export function recoverySessionHeaders(): Record<string, string> {
  const id = readRecoverySessionId();
  return id ? { [RECOVERY_SESSION_HEADER]: id } : {};
}

/**
 * The three session fields every initiation response carries (D-143 rule 6).
 *
 * `sessionRequestsUsed` counts requests made from THIS browser. It is NOT the
 * account's OTP count, NOT "OTPs sent", NOT "emails sent" and NOT remaining
 * account quota — the authoritative account figure is disclosed only inside the
 * OTP email, where only the mailbox owner can read it.
 */
export interface RecoverySessionFields {
  recoverySessionId: string;
  sessionRequestsUsed: number;
  sessionRequestLimit: number;
}
